import { useMemo, useState } from 'react';
import without from 'lodash/without';
import { TagGroupField } from 'lib/iterable-hook-form/FormField';
import {
  FormSectionContent,
  StyledLeftTooltip,
} from 'modules/workflowNodeEditors/EditorBody.styled';
import { InputField } from 'lib/iterable-hook-form';
import { InputText } from 'ui/inputs';
import { useCampaign, useFeatureFlags } from '../BaseSendEditor.hooks';
import { getLabelConfigsFromNames, getLabelNames } from '../BaseSendEditor.utils';
import CampaignNameLabel from './CampaignNameLabel';
import { StyledFormField, Subtitle, StyledTagGroup } from './CampaignNameSection.styles';

export interface CampaignNameSectionProps {
  /** Campaign ID */
  campaignId?: number;
  subtitle?: string;
}

export const MAX_CAMPAIGN_NAME_LENGTH = 255;
export const CAMPAIGN_NAME_TEST_ID = 'campaign-name-input';
export const SUBTITLE_TEST_ID = 'campaign-name-subtitle';

export const CampaignNameSection = ({ campaignId, subtitle }: CampaignNameSectionProps) => {
  const { isSendEditorImprovementsEnabled } = useFeatureFlags();

  const {
    campaign = {},
    campaignLabels = [],
    updateCampaign,
    isReadOnly: isDisabled,
  } = useCampaign();

  const { labels = [], name: campaignName } = campaign;

  const [isFocused, setIsFocused] = useState(false);
  const allLabelNames = useMemo(
    () => campaignLabels.map(({ name }) => name as string),
    [campaignLabels],
  );

  const { selectedLabels, unselectedLabels } = useMemo(() => {
    const selected = getLabelNames(labels);
    return { selectedLabels: selected, unselectedLabels: without(allLabelNames, ...selected) };
  }, [labels, allLabelNames]);

  return (
    <FormSectionContent>
      <StyledFormField
        labelText={
          <CampaignNameLabel campaignId={campaignId} shouldDisplayID={!isFocused && !!campaignId} />
        }
        inputId="campaign-name-field"
        restrictionText={isFocused ? `${MAX_CAMPAIGN_NAME_LENGTH} characters` : undefined}
        isFocused={isFocused}
      >
        <StyledLeftTooltip isEnabled={isDisabled} tooltipContent={campaignName}>
          {isSendEditorImprovementsEnabled ? (
            <InputField
              name="name"
              id="campaign-name-field"
              data-test={CAMPAIGN_NAME_TEST_ID}
              onFocus={() => setIsFocused(true)}
              onBlurField={() => setIsFocused(false)}
              type="text"
              isDisabled={isDisabled}
              maxLength={MAX_CAMPAIGN_NAME_LENGTH}
            />
          ) : (
            <InputText
              id="campaign-input"
              data-test={CAMPAIGN_NAME_TEST_ID}
              onChange={(name) => {
                if (name.length <= MAX_CAMPAIGN_NAME_LENGTH) {
                  updateCampaign({ name });
                }
              }}
              // @ts-ignore
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              type="text"
              value={campaignName || ''}
              isDisabled={isDisabled}
            />
          )}
        </StyledLeftTooltip>
      </StyledFormField>
      {subtitle && <Subtitle data-test={SUBTITLE_TEST_ID}>{subtitle}</Subtitle>}
      {/* BasicSelect component used in dropdown of TagGroup has some rendering issues,
              this check is a temp. workaround until component fix goes out.
              To be updated in WF-719 */}
      {/* Display the label selector if there are label options to select */}
      {campaignLabels.length > 0 &&
        (isSendEditorImprovementsEnabled ? (
          <TagGroupField
            name={'labels'}
            isAutoSaveEnabled
            isEditable={!isDisabled}
            tags={unselectedLabels}
          />
        ) : (
          <StyledTagGroup
            isAutoSaveEnabled={true}
            onUpdate={(addedTags) => {
              // parses strings to label configs for update
              updateCampaign({ labels: getLabelConfigsFromNames(addedTags, campaignLabels) });
            }}
            tags={unselectedLabels}
            value={selectedLabels}
            isEditable={!isDisabled}
          />
        ))}
    </FormSectionContent>
  );
};

export default CampaignNameSection;
